/**
 * EditorPane
 *
 * A real code editor next to a real shell. The learner can fix the file the
 * mission is about without leaving the browser, and every save is a write into
 * the same working tree the terminal and the grader see — which is why a
 * successful save calls `onSaved`, so the caller can refresh `git status` and
 * the graph moves the moment the file changes.
 *
 * CodeMirror is not a React component. One `EditorView` is created on mount
 * and destroyed on unmount; opening a file swaps the *state* underneath it
 * (`view.setState`), which replaces the document, the language and — the point
 * — the undo history, so Ctrl+Z can never walk backwards into a file that is
 * no longer open.
 *
 * Run and Run tests save, then hand a command to the host, which types it
 * into the terminal (lib/run.ts says which command and why it is typed rather
 * than run). The editor never runs anything itself: it has no shell, and a
 * second place that runs code would be a second place whose output the
 * learner has to learn to read.
 *
 * Two roads lead back in. A Check saves the buffer before it grades
 * (`flushForCheck`), and a `path:line` clicked in the terminal opens here with
 * the caret on that line (`openAt`).
 */
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, type Ref } from 'react';
import { EditorState, Prec } from '@codemirror/state';
import { EditorView, keymap } from '@codemirror/view';
import { syntaxHighlighting } from '@codemirror/language';
import { basicSetup } from 'codemirror';
import { languageFor, tangoHighlight, tangoTheme } from './syntax';
import { api } from '../../api';
import { sound } from '../../lib/sound';
import { ltr } from '../../lib/format';
import { registerEditorFlush } from '../../lib/editor-flush';
import { runFileCommand, testCommand } from '../../lib/run';
import { useLocale, useStrings } from '../../state/useLocale';
import type { Strings } from '../../lib/strings';
import type { FileEntry } from '../../types';
import { FileTree } from './FileTree';
import type { EditorPaneProps } from '../contracts';

/** What the host pane shows in its header while the editor tab is active. */
export interface EditorStatus {
  path: string | null;
  dirty: boolean;
}

/** How handing a command to the terminal went (TerminalPane's `type`). */
export type RunOutcome = 'typed' | 'busy' | 'detached';

/** Which button asked: the open file, or the workspace's tests. */
export type RunKind = 'file' | 'tests';

/** What a host (WorkPane) can ask the editor to do imperatively. */
export interface EditorHandle {
  /**
   * Open a file with the caret on a line, and a column when there is one,
   * both counted from 1 as a stack trace counts them: a frame clicked in the
   * terminal's output.
   */
  openAt(path: string, line: number, col?: number): Promise<void>;
}

/* ── Component ────────────────────────────────────────────────────────────── */

/**
 * Implements `EditorPaneProps`. `onStatus` is an optional extension, the way
 * `TerminalPane` takes an optional ref: the host pane uses it to render the
 * open file in its header, and a caller that passes only the contract props
 * still works. `onRun` is the same kind of extension: without a terminal to
 * type into there is nothing to run with, and the Run buttons are not drawn.
 */
export function EditorPane({
  sessionId,
  cwd,
  onSaved,
  onStatus,
  onRun,
  onEntries: onListing,
  resetKey = 0,
  workspaceKey = 0,
  ref,
}: EditorPaneProps & {
  onStatus?: (status: EditorStatus) => void;
  onRun?: (command: string, kind: RunKind) => Promise<RunOutcome>;
  /** Every listing of the workspace the tree reads, for a host that resolves paths against it. */
  onEntries?: (entries: readonly FileEntry[]) => void;
  resetKey?: number;
  /** Finished terminal or agent commands, without discarding an unsaved buffer. */
  workspaceKey?: number;
  ref?: Ref<EditorHandle>;
}) {
  // Stable per language — the catalogue is a module constant — so putting it
  // in the callback deps below costs nothing until somebody switches.
  const s: Strings['play']['editor'] = useStrings().play.editor;
  const dir = useLocale().dir;
  const hostRef = useRef<HTMLDivElement | null>(null);
  const viewRef = useRef<EditorView | null>(null);

  const [path, setPath] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  // `node --test` when the workspace has a test in it (lib/run.ts), from the
  // tree's own listing so that knowing costs no second trip to the machine.
  const [tests, setTests] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  // Why the last Run typed nothing. Not an error: nothing failed, the
  // terminal was simply not in a state to take a command.
  const [notice, setNotice] = useState<string | null>(null);

  // Content as it exists on disk. The dirty flag is the buffer measured
  // against this, not a "has been typed in" flag, so undoing back to the
  // saved text correctly reports clean again.
  const savedRef = useRef('');
  const pathRef = useRef<string | null>(null);
  const dirtyRef = useRef(false);
  // The file on its way in, while it loads. The buffer still on screen is
  // spoken for from the moment the learner agreed to lose it, and nothing may
  // save it in the gap — a Ctrl+S, a Run or a Check's flush there would write
  // the very text they had just discarded.
  const openingRef = useRef<string | null>(null);

  /* --- The view ----------------------------------------------------------- */

  // A stable set of extensions, closed over refs only, so `setState` can build
  // a fresh state per file without rebuilding any behaviour.
  const saveRef = useRef<() => void>(() => {});
  const runFileRef = useRef<() => void>(() => {});
  const baseExtensions = useMemo(
    () => [
      // Highest precedence: nothing in basicSetup may claim Mod-s first, and
      // `preventDefault` is what stops the browser's own save dialog.
      Prec.highest(
        keymap.of([
          {
            key: 'Mod-s',
            preventDefault: true,
            run: () => {
              saveRef.current();
              return true;
            },
          },
          // Run, where every IDE learners will meet puts it. Claimed even
          // when the open file has nothing to run it: F5 is the browser's
          // reload, and a reload here reseeds the level and throws the
          // learner's work away.
          {
            key: 'F5',
            preventDefault: true,
            run: () => {
              runFileRef.current();
              return true;
            },
          },
          // Ctrl+Enter is Check everywhere on this screen (Play.tsx), and the
          // event still reaches that listener: claiming it here stops only
          // basicSetup's blank line. That line used to be harmless clutter;
          // now that a Check saves the buffer first, it would be written into
          // the learner's file a moment before the file was graded.
          { key: 'Mod-Enter', run: () => true },
        ]),
      ),
      basicSetup,
      tangoTheme,
      syntaxHighlighting(tangoHighlight),
      EditorView.updateListener.of((update) => {
        if (!update.docChanged) return;
        const next = update.state.doc.toString() !== savedRef.current;
        if (next === dirtyRef.current) return;
        dirtyRef.current = next;
        setDirty(next);
      }),
    ],
    [],
  );

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const view = new EditorView({
      state: EditorState.create({ doc: '', extensions: [...baseExtensions, EditorState.readOnly.of(true)] }),
      parent: host,
    });
    viewRef.current = view;
    return () => {
      viewRef.current = null;
      // Removes the DOM, the observers and the key handlers. Without this,
      // strict mode's second mount would stack a second editor on the first.
      view.destroy();
    };
  }, [baseExtensions]);

  // A new session is a new workspace, and so is a reset: drop the buffer
  // rather than leave a file open over a tree that no longer contains it.
  // Without the reset half, an unsaved buffer survives "Reset workspace" and
  // the next Ctrl+S writes pre-reset content back over the rebuilt tree,
  // quietly undoing part of the reset the grader is about to read.
  useEffect(() => {
    savedRef.current = '';
    pathRef.current = null;
    dirtyRef.current = false;
    setPath(null);
    setDirty(false);
    setError(null);
    setNotice(null);
    setTests(null);
    viewRef.current?.setState(
      EditorState.create({ doc: '', extensions: [...baseExtensions, EditorState.readOnly.of(true)] }),
    );
    setRefreshKey((k) => k + 1);
  }, [sessionId, resetKey, baseExtensions]);

  /* --- Opening ------------------------------------------------------------ */

  const openFile = useCallback(
    async (next: string) => {
      if (next === pathRef.current) {
        viewRef.current?.focus();
        return;
      }
      // Isolated before they go in: a bare `window.confirm` has no markup to
      // hang `dir` on, so the two paths carry their own direction with them.
      if (dirtyRef.current && !window.confirm(s.confirmDiscard(ltr(pathRef.current ?? ''), ltr(next)))) {
        return;
      }
      setBusy(true);
      setError(null);
      setNotice(null);
      openingRef.current = next;
      try {
        const file = await api.readFile(sessionId, next);
        const view = viewRef.current;
        if (!view) return;
        savedRef.current = file.content;
        pathRef.current = next;
        dirtyRef.current = false;
        view.setState(
          EditorState.create({ doc: file.content, extensions: [...baseExtensions, ...languageFor(next, file.content)] }),
        );
        setPath(next);
        setDirty(false);
        // Focus is taken in the effect below, not here: the host is still
        // `hidden` until React paints the swap, and a hidden element cannot
        // take focus.
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : s.openFailed);
      } finally {
        openingRef.current = null;
        setBusy(false);
      }
    },
    [sessionId, baseExtensions, s],
  );

  /* --- Saving ------------------------------------------------------------- */

  /** True when what is on disk is now what is in the buffer, including when nothing needed writing. */
  const save = useCallback(async (): Promise<boolean> => {
    const view = viewRef.current;
    const current = pathRef.current;
    if (!view || !current || !dirtyRef.current || openingRef.current !== null) return true;
    const content = view.state.doc.toString();
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await api.writeFile(sessionId, current, content);
      // Only now is the buffer clean. A failed write leaves the flag set on
      // purpose: the learner's work is still the only copy. And only if the
      // buffer is still this file's: another one opened while the write was
      // on its way, the write stands, but the clean-or-dirty state on screen
      // is the new file's and is not this save's to set.
      if (pathRef.current === current) {
        savedRef.current = content;
        const stillDirty = view.state.doc.toString() !== content;
        dirtyRef.current = stillDirty;
        setDirty(stillDirty);
      }
      sound.save();
      // Saving changes `git status`, so the graph has to be told.
      onSaved(current);
      setRefreshKey((k) => k + 1);
      return true;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : s.saveFailed);
      return false;
    } finally {
      setSaving(false);
    }
  }, [sessionId, onSaved, s]);

  // The keymap calls through this ref, so the binding never has to be rebuilt.
  useEffect(() => {
    saveRef.current = () => void save();
  }, [save]);

  /* --- Running ------------------------------------------------------------ */

  const runRef = useRef(onRun);
  useEffect(() => {
    runRef.current = onRun;
  });
  // A ref as well as the state: a second F5 lands before React has
  // re-rendered with the button disabled.
  const runningRef = useRef(false);

  /**
   * Save, then hand the command over. Saving first because the command runs
   * what is on disk, and a learner who presses Run expects the code they are
   * looking at — every IDE they will use saves before it runs. A save that
   * fails stops the run: running the previous version would report on code
   * that is not on the screen.
   */
  const run = useCallback(
    async (command: string | null, kind: RunKind) => {
      const host = runRef.current;
      if (!command || !host || runningRef.current) return;
      runningRef.current = true;
      setRunning(true);
      setNotice(null);
      try {
        if (!(await save())) return;
        const outcome = await host(command, kind);
        if (outcome === 'busy') setNotice(s.runBusy);
        else if (outcome === 'detached') setNotice(s.runDetached);
      } finally {
        runningRef.current = false;
        setRunning(false);
      }
    },
    [save, s],
  );

  useEffect(() => {
    runFileRef.current = () => void run(pathRef.current ? runFileCommand(pathRef.current) : null, 'file');
  }, [run]);

  const listingRef = useRef(onListing);
  useEffect(() => {
    listingRef.current = onListing;
  });
  const onEntries = useCallback((entries: readonly FileEntry[]) => {
    const next = testCommand(entries);
    setTests((prev) => (prev === next ? prev : next));
    listingRef.current?.(entries);
  }, []);

  /* --- Checking ----------------------------------------------------------- */

  /**
   * Before a Check grades the workspace (lib/editor-flush.ts): the learner is
   * asking about the code on their screen, so that goes to disk first.
   *
   * Unless the file changed on disk since the editor last read or wrote it —
   * a `sed`, a `git checkout`, `nano` in the terminal. Then the disk holds
   * the newer work and the buffer is the stale copy, and writing it would
   * quietly undo what the learner did in the shell a moment before it was
   * graded. So nothing is written, the Check reads the disk, and the editor
   * says which version that was. Costs one read, and only when there is
   * something unsaved.
   */
  const flushForCheck = useCallback(async () => {
    const current = pathRef.current;
    if (!current || !dirtyRef.current || openingRef.current !== null) return;
    let disk: string | null;
    try {
      disk = (await api.readFile(sessionId, current)).content;
    } catch {
      // Deleted, or no longer text: either way not the file the editor opened.
      disk = null;
    }
    if (pathRef.current !== current) return;
    if (disk !== savedRef.current) {
      setNotice(s.checkedDisk(current));
      return;
    }
    await save();
  }, [sessionId, save, s]);

  const flushRef = useRef(flushForCheck);
  useEffect(() => {
    flushRef.current = flushForCheck;
  });
  useEffect(() => registerEditorFlush(sessionId, () => flushRef.current()), [sessionId]);

  /* --- Opening at a line -------------------------------------------------- */

  // Where to put the caret once the file it belongs to has been painted. Set
  // by `openAt`, spent by `reveal`: the view cannot measure where a line is
  // while its host is still hidden, and a frame clicked in the terminal
  // usually arrives with the editor tab not yet showing.
  const revealRef = useRef<{ path: string; line: number; col: number } | null>(null);

  const reveal = useCallback(() => {
    const want = revealRef.current;
    const view = viewRef.current;
    if (!want || !view || want.path !== pathRef.current) return;
    revealRef.current = null;
    const doc = view.state.doc;
    const line = doc.line(Math.min(Math.max(1, want.line), doc.lines));
    const at = Math.min(line.from + Math.max(0, want.col - 1), line.to);
    view.requestMeasure();
    view.dispatch({ selection: { anchor: at }, effects: EditorView.scrollIntoView(at, { y: 'center' }) });
    view.focus();
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      openAt: async (next, line, col = 1) => {
        revealRef.current = { path: next, line, col };
        // Already open: nothing will re-render, so wait out the frame in
        // which the tab is shown. Otherwise the open itself ends in `reveal`.
        if (next === pathRef.current) requestAnimationFrame(reveal);
        else await openFile(next);
      },
    }),
    [openFile, reveal],
  );

  /* --- Host wiring -------------------------------------------------------- */

  // Runs once the newly opened file has been painted. CodeMirror cannot
  // measure — or focus — inside a `display:none` container, so both the
  // re-measure and the caret land here rather than at the end of `openFile`.
  useEffect(() => {
    if (!path) return;
    const view = viewRef.current;
    if (!view) return;
    view.requestMeasure();
    view.focus();
    reveal();
  }, [path, reveal]);

  // Held in a ref and fired only on a real change, so a host that passes an
  // inline callback and stores the result in state cannot loop.
  const statusRef = useRef(onStatus);
  useEffect(() => {
    statusRef.current = onStatus;
  });
  useEffect(() => {
    statusRef.current?.({ path, dirty });
  }, [path, dirty]);

  const dirtyPath = dirty ? path : null;
  const fileCommand = onRun && path ? runFileCommand(path) : null;
  const testsCommand = onRun ? tests : null;

  return (
    <div className="editor">
      <FileTree
        sessionId={sessionId}
        selected={path}
        dirtyPath={dirtyPath}
        rootLabel={cwd}
        refreshKey={refreshKey + workspaceKey}
        onOpen={(next) => void openFile(next)}
        onEntries={onEntries}
      />

      <div className="editor-main">
        <div className="editor-bar">
          <span className="editor-file" title={path ?? undefined}>
            {path ? (
              <>
                {/* A path is the machine's and keeps its own direction, or
                    bidi reorders `src/cart.js` into `cart.js/src`. */}
                <span className="editor-file-path" dir="ltr">
                  {path}
                </span>
                {dirty && <span className="editor-dirty" aria-hidden="true" />}
                <span className="editor-file-state">{dirty ? s.unsaved : s.saved}</span>
              </>
            ) : (
              <span className="editor-file-none">{s.noFile}</span>
            )}
          </span>

          <div className="editor-actions">
            {error && (
              <span className="editor-error" role="alert">
                {error}
              </span>
            )}
            {/* Drawn only when there is something to run: a Run button on a
                README would be a button that can only say no. The title is
                the exact line it types. */}
            {fileCommand && (
              <button
                type="button"
                className="btn editor-save editor-run"
                onClick={() => void run(fileCommand, 'file')}
                disabled={running || saving}
                title={s.runTitle(fileCommand)}
              >
                {s.run}
              </button>
            )}
            {testsCommand && (
              <button
                type="button"
                className="btn editor-save editor-run"
                onClick={() => void run(testsCommand, 'tests')}
                disabled={running || saving}
                title={s.runTestsTitle(testsCommand)}
              >
                {s.runTests}
              </button>
            )}
            <button
              type="button"
              className="btn editor-save"
              onClick={() => void save()}
              disabled={!path || !dirty || saving}
              title={s.saveTitle}
            >
              {saving ? s.saving : s.save}
            </button>
          </div>
        </div>

        {/* Prose in the page's language inside a strip that is pinned left
            to right for the code's sake (locale.css §3), so it carries its
            own direction. */}
        {notice && (
          <p className="editor-notice" role="status" dir={dir}>
            {notice}
          </p>
        )}

        <div className="editor-stage">
          {/* Code is left to right in every language (docs/i18n.md §7), and
              CodeMirror reads the direction off this element: without it the
              gutter, the caret and every line would mirror in Arabic. */}
          <div className="editor-host" ref={hostRef} dir="ltr" hidden={!path} />
          {!path && (
            <div className="editor-blank">
              <span className="label">{s.blankLabel}</span>
              <p className="note">{busy ? s.opening : s.blankHint}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default EditorPane;
